/**
 * Session-expiry handling (fix, Jul 30 2026).
 *
 * Nothing in the app handled a 401. Every API client rethrew the server's message verbatim, so once
 * the auth cookie lapsed the UI showed "Unauthorized - No token provided" — a developer string —
 * wherever the user happened to be. It was reported as the calendar being broken; it was really an
 * expired login, and it would have been reported next as messages, documents or payments breaking.
 *
 * Rather than retrofit ~17 API clients (and rely on every future one remembering), this wraps fetch
 * once: any 401 from our API sends the user to the right login screen with a plain explanation.
 */

const SESSION_EXPIRED_KEY = 'session_expired';

/** Login screens for the two audiences — see routers/DefaultRouter. */
const MANAGER_LOGIN = '/manager-login';
const CLIENT_LOGIN = '/login';

/**
 * Endpoints where a 401 is a legitimate answer rather than a dead session. Note these are mounted at
 * the root of /api/v1 (NOT under /api/v1/auth/, despite living in AuthRouter):
 *  - login returns 401 for a wrong password; redirecting would wipe the form.
 *  - `me` / `manager/me` are the session probes App.tsx fires on every page load, including for
 *    anonymous visitors on public pages. Treating those as an expired session would bounce a
 *    first-time visitor to a login screen claiming their session had expired.
 *
 * Matched as exact pathnames, not substrings — `/api/v1/message` contains `/api/v1/me`, and a
 * substring test would silently exempt every message endpoint from the guard.
 */
const EXEMPT_API_PATHS = new Set([
  '/api/v1/login',
  '/api/v1/manager-login',
  '/api/v1/logout',
  '/api/v1/me',
  '/api/v1/manager/me',
]);

const API_PREFIX = '/api/v1/';

/** Pages that must never redirect — they ARE the destination. */
const NO_REDIRECT_PATHS = [
  MANAGER_LOGIN,
  CLIENT_LOGIN,
  '/register',
  '/reset-password',
  '/manager-reset-password',
  '/finish-account-creation',
  '/verify-account',
];

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/** The request's pathname, or null if it isn't a call to our API. Relative URLs resolve against origin. */
export function apiPathnameOf(url: string): string | null {
  let pathname: string;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return trimmed.startsWith(API_PREFIX) ? trimmed : null;
}

/** Whether a 401 on this URL means the session died (vs. a normal "not signed in" answer). */
export function isSessionExpiryUrl(url: string): boolean {
  const path = apiPathnameOf(url);
  return path !== null && !EXEMPT_API_PATHS.has(path);
}

/**
 * Manager pages live under paths like /user, /tickets, /calendar; the client portal is a smaller
 * set. We can't read the httpOnly cookie, so route by where the user currently is and fall back to
 * the manager screen — staff hit this far more often, and a client sent to /manager-login would at
 * least still see a login form rather than an error.
 */
function loginPathForCurrentLocation(): string {
  const path = window.location.pathname.toLowerCase();
  const clientAreas = ['/tickets', '/ticket', '/documents', '/messages', '/settings', '/new-ticket'];
  const isClientArea = clientAreas.some((p) => path === p || path.startsWith(`${p}/`));
  return isClientArea ? CLIENT_LOGIN : MANAGER_LOGIN;
}

/** Set by the guard, read once by the login screens. */
export function consumeSessionExpiredFlag(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1') {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      return true;
    }
  } catch {
    // sessionStorage can throw in private-browsing modes; the message is a nicety, not the fix.
  }
  return false;
}

let redirecting = false;

function redirectToLogin(): void {
  // Several requests usually fail together (a dashboard fires many at once) — only act on the first.
  if (redirecting) return;

  const currentPath = window.location.pathname.toLowerCase();
  if (NO_REDIRECT_PATHS.some((p) => currentPath === p || currentPath.startsWith(`${p}/`))) {
    return;
  }

  redirecting = true;
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
  } catch {
    // Non-fatal — proceed to the login screen regardless.
  }
  window.location.assign(loginPathForCurrentLocation());
}

/**
 * Wrap window.fetch to catch expired sessions. Installed once at start-up; safe to call twice
 * (guarded), and passes every response through untouched so existing error handling still runs.
 */
export function installSessionGuard(): void {
  const w = window as unknown as { __sessionGuardInstalled?: boolean };
  if (w.__sessionGuardInstalled) return;
  w.__sessionGuardInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input as RequestInfo, init);

    if (response.status === 401 && isSessionExpiryUrl(requestUrl(input))) {
      redirectToLogin();
    }

    return response;
  };
}
